import { logs, SeverityNumber, type AnyValueMap } from '@opentelemetry/api-logs';
import { trace } from '@opentelemetry/api';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

const LOG_LEVELS: Record<LogLevel, number> = { debug: 0, info: 1, warn: 2, error: 3 };

const SEVERITY_MAP: Record<LogLevel, SeverityNumber> = {
  debug: SeverityNumber.DEBUG,
  info: SeverityNumber.INFO,
  warn: SeverityNumber.WARN,
  error: SeverityNumber.ERROR
};

export class Logger {
  private readonly name: string;
  private readonly otelLogger;
  private minLevel: LogLevel = 'info';
  private consoleEnabled = true;
  private otelEnabled = true;

  constructor(name: string) {
    this.name = name;
    this.otelLogger = logs.getLogger(name);
  }

  configure(options: { level?: LogLevel; console?: boolean; otel?: boolean }): this {
    if (options.level != null) this.minLevel = options.level;
    if (options.console != null) this.consoleEnabled = options.console;
    if (options.otel != null) this.otelEnabled = options.otel;
    return this;
  }

  child(childName: string): Logger {
    const child = new Logger(`${this.name}:${childName}`);
    child.minLevel = this.minLevel;
    child.consoleEnabled = this.consoleEnabled;
    child.otelEnabled = this.otelEnabled;
    return child;
  }

  debug(message: string, attrs?: AnyValueMap): void {
    this.emit('debug', message, attrs);
  }

  info(message: string, attrs?: AnyValueMap): void {
    this.emit('info', message, attrs);
  }

  warn(message: string, attrs?: AnyValueMap): void {
    this.emit('warn', message, attrs);
  }

  error(message: string, attrs?: AnyValueMap): void {
    this.emit('error', message, attrs);
  }

  private emit(level: LogLevel, message: string, attrs: AnyValueMap = {}): void {
    if (LOG_LEVELS[level] < LOG_LEVELS[this.minLevel]) return;

    const enrichedAttrs: AnyValueMap = { ...attrs };

    // Add trace context if available
    const traceContext = getTraceContext();
    if (traceContext.spanId) {
      Object.assign(enrichedAttrs, traceContext);
    }

    if (this.consoleEnabled) {
      const fn = level === 'info' ? console.log : console[level];
      if (Object.keys(enrichedAttrs).length === 0) {
        fn(message);
      } else {
        fn(message, enrichedAttrs);
      }
    }

    if (this.otelEnabled) {
      this.otelLogger.emit({
        body: message,
        severityNumber: SEVERITY_MAP[level],
        severityText: level.toUpperCase(),
        attributes: { ...enrichedAttrs, 'log.namespace': this.name }
      });
    }
  }
}

export const createLogger = (name: string): Logger => new Logger(name);

export const getTraceContext = (): { spanId?: string; traceId?: string } => {
  const span = trace.getActiveSpan();
  if (span) {
    const spanContext = span.spanContext();
    return {
      spanId: spanContext.spanId,
      traceId: spanContext.traceId
    };
  }
  return {};
};
